import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyBeforeUpdateEmail, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useAuth } from './AuthContext';
import { 
  FiAlertCircle, 
  FiCheckCircle, 
  FiSend,
  FiArrowLeft,
  FiInfo
} from 'react-icons/fi';
import './auth-pages.css';

function ChangeEmailPage() {
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { hasReauthenticated } = useAuth();

  useEffect(() => {
    if (!hasReauthenticated) {
      navigate('/dashboard/reauthenticate');
    }
  }, [hasReauthenticated, navigate]);

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = async () => {
    // Reset state
    setError('');
    setSuccess('');
    
    // Validate input
    if (!newEmail) {
      setError('Please enter an email address.');
      return;
    }

    if (!validateEmail(newEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (newEmail === auth.currentUser.email) {
      setError('New email must be different from your current email.');
      return;
    }

    setIsSubmitting(true);
  
    try {
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
      setSuccess('A verification email has been sent to your new email address. Please verify it to complete the update.');
      
      // After 3 seconds, sign out the user and redirect to the login page
      setTimeout(() => {
        signOut(auth);
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(`Failed to initiate email update: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Change Email</h1>
          <p>Update your email address</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{success}</span>
          </div>
        )}

        {!success && (
          <div className="auth-warning" style={{ marginBottom: '1.5rem' }}>
            <FiInfo />
            <span>
              After changing your email, you'll need to verify your new email address and sign in again.
            </span>
          </div>
        )}

        {!success && (
          <form className="auth-form">
            <div className="form-group">
              <input
                type="email"
                className="form-control"
                placeholder="New Email Address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>

            <button 
              type="button" 
              className="auth-btn primary-btn" 
              onClick={handleEmailChange}
              disabled={isSubmitting}
            >
              <FiSend /> {isSubmitting ? 'Sending...' : 'Update Email'}
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => navigate('/dashboard/profile')}
              disabled={isSubmitting}
            >
              <FiArrowLeft /> Back to Profile
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ChangeEmailPage;